import {
  apiClient,
  createErrorResponse,
  createSuccessResponse,
  handleApiError,
} from "../../../../../shared/api";
import { saleReopenConfig } from "../config/sale-reopen.config.js";
import {
  buildApproveReopenPayload,
  buildRejectReopenPayload,
  buildRequestReopenPayload,
  normalizeSaleId,
} from "../lib/build-sale-reopen-request-payloads.js";
import { toRecipientSelectionOptions } from "../lib/to-recipient-selection-options.js";

/**
 * Serviço HTTP do fluxo request/approve/reject de reabertura de venda.
 */
export class SaleReopenService {
  constructor() {
    this.config = saleReopenConfig;
  }

  /**
   * Lista opções tokenizadas de destinatário (`GET /sale/recipient-selection-options`).
   * @param {string} [role]
   * @param {RequestInit} [requestOptions]
   */
  async getRecipientSelectionOptions(
    role = this.config.recipientRole,
    requestOptions,
  ) {
    const roleValue =
      typeof role === "string" && role.trim() !== ""
        ? role.trim()
        : this.config.recipientRole;

    if (roleValue !== this.config.recipientRole) {
      return createErrorResponse(
        "role ausente ou não suportada neste fluxo",
        400,
        "INVALID_ROLE",
      );
    }

    try {
      const params = new URLSearchParams();
      params.append("role", roleValue);

      const endpoint = `${this.config.paths.recipientSelectionOptions}?${params.toString()}`;
      const response = await apiClient.get(endpoint, requestOptions);
      const options = toRecipientSelectionOptions(response);

      return createSuccessResponse(
        options,
        200,
        "Opções de destinatário carregadas com sucesso",
      );
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * Solicita reabertura (`POST /sale/{idsale}/request-reopen`) — status 202.
   * @param {string|number} idsale
   * @param {{ recipientToken: string, reason?: string, observation?: string, requesterReason?: string }} body
   * @param {RequestInit} [requestOptions]
   */
  async requestReopen(idsale, body, requestOptions) {
    const saleId = normalizeSaleId(idsale);
    if (saleId == null) {
      return createErrorResponse("idsale inválido", 400, "INVALID_IDSALE");
    }

    const payload = buildRequestReopenPayload(
      body,
      this.config.reasonMaxLength,
    );
    if (!payload) {
      return createErrorResponse(
        "recipientToken é obrigatório",
        400,
        "INVALID_RECIPIENT_TOKEN",
      );
    }

    try {
      const response = await apiClient.post(
        this.config.paths.requestReopen(saleId),
        payload,
        requestOptions,
      );
      const data = response?.data ?? response ?? null;
      const message =
        response?.message ??
        data?.message ??
        "Solicitação de reabertura enviada.";

      return createSuccessResponse(data, 202, message);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * Aprova reabertura (`POST /sale/{idsale}/approve-reopen`).
   * @param {string|number} idsale
   * @param {{ notifyUserId?: number, requesterUserId?: number, requestedByUserId?: number }} body
   * @param {RequestInit} [requestOptions]
   */
  async approveReopen(idsale, body, requestOptions) {
    const saleId = normalizeSaleId(idsale);
    if (saleId == null) {
      return createErrorResponse("idsale inválido", 400, "INVALID_IDSALE");
    }

    const payload = buildApproveReopenPayload(body);
    if (!payload) {
      return createErrorResponse(
        "notifyUserId inválido",
        400,
        "INVALID_NOTIFY_USER_ID",
      );
    }

    try {
      const response = await apiClient.post(
        this.config.paths.approveReopen(saleId),
        payload,
        requestOptions,
      );
      const data = response?.data ?? response ?? null;
      const message =
        response?.message ??
        data?.message ??
        "Venda reaberta com sucesso.";

      return createSuccessResponse(data, 200, message);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * Recusa reabertura (`POST /sale/{idsale}/reject-reopen`).
   * @param {string|number} idsale
   * @param {{ notifyUserId?: number, requesterUserId?: number, requestedByUserId?: number, reason?: string, rejectReason?: string, observation?: string }} body
   * @param {RequestInit} [requestOptions]
   */
  async rejectReopen(idsale, body, requestOptions) {
    const saleId = normalizeSaleId(idsale);
    if (saleId == null) {
      return createErrorResponse("idsale inválido", 400, "INVALID_IDSALE");
    }

    const payload = buildRejectReopenPayload(
      body,
      this.config.reasonMaxLength,
    );
    if (!payload) {
      return createErrorResponse(
        "notifyUserId inválido",
        400,
        "INVALID_NOTIFY_USER_ID",
      );
    }

    try {
      const response = await apiClient.post(
        this.config.paths.rejectReopen(saleId),
        payload,
        requestOptions,
      );
      const data = response?.data ?? response ?? null;
      const message =
        response?.message ??
        data?.message ??
        "Solicitação de reabertura recusada.";

      return createSuccessResponse(data, 200, message);
    } catch (error) {
      return handleApiError(error);
    }
  }
}

const saleReopenService = new SaleReopenService();
export { saleReopenService };
export default saleReopenService;
